import { useState } from "react";
import { speakText } from "../../../utils/audioHelpers";

function VolumeIcon({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
    );
}

function AlphabetGrid({ letters, langTag, lang_id }) {
    const [selectedLetter, setSelectedLetter] = useState(null);

    const isArabic = parseInt(lang_id, 10) === 6;

    const playAudio = (text) => {
        speakText(text, langTag);
    };

    return (
        <div className={`alphabet-grid-container ${isArabic ? "alphabet-grid--rtl" : ""}`} dir={isArabic ? "rtl" : "ltr"}>
            <div className="alphabet-grid">
                {letters.map((item) => {
                    const characterDisplay = item.character_upper && item.character_lower
                        ? `${item.character_upper} ${item.character_lower}`
                        : item.character;

                    return (
                        <div
                            key={item.id}
                            className="alphabet-card"
                            onClick={() => {
                                setSelectedLetter(item);
                                playAudio(item.pronunciation || item.character);
                            }}
                        >
                            <div className="alphabet-card__main-char">{characterDisplay}</div>
                            <div className="alphabet-card__name">{item.name}</div>
                            <div className="alphabet-card__translit">/{item.transliteration}/</div>
                            <button
                                className="alphabet-card__audio-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    playAudio(item.pronunciation || item.character);
                                }}
                                aria-label={`Play sound for ${item.name}`}
                            >
                                <VolumeIcon size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {selectedLetter && (
                <div className="alphabet-modal-overlay" onClick={() => setSelectedLetter(null)}>
                    <div className="alphabet-modal" onClick={(e) => e.stopPropagation()} dir={isArabic ? "rtl" : "ltr"}>
                        <button className="alphabet-modal__close" onClick={() => setSelectedLetter(null)} aria-label="Close modal">&times;</button>

                        <div className="alphabet-modal__header">
                            <h2 className="alphabet-modal__char">
                                {selectedLetter.character_upper && selectedLetter.character_lower
                                    ? `${selectedLetter.character_upper} ${selectedLetter.character_lower}`
                                    : selectedLetter.character}
                            </h2>
                            <button
                                className="alphabet-modal__speaker-btn"
                                onClick={() => playAudio(selectedLetter.pronunciation || selectedLetter.character)}
                                aria-label="Listen to sound"
                            >
                                <VolumeIcon size={24} /> Listen
                            </button>
                        </div>

                        <div className="alphabet-modal__details">
                            <div className="alphabet-modal__detail-item">
                                <span className="detail-label">Letter Name:</span>
                                <span className="detail-value">{selectedLetter.name}</span>
                            </div>
                            <div className="alphabet-modal__detail-item">
                                <span className="detail-label">Phonetic Sound:</span>
                                <span className="detail-value">/{selectedLetter.transliteration}/ ({selectedLetter.ipa})</span>
                            </div>

                            {/* Show positional forms for Arabic */}
                            {isArabic && selectedLetter.positional_forms && (
                                <div className="alphabet-positional-section">
                                    <h3>Positional Forms</h3>
                                    <div className="alphabet-positional-grid">
                                        {Object.entries(JSON.parse(selectedLetter.positional_forms || "{}")).map(([pos, shape]) => (
                                            <div key={pos} className="positional-card">
                                                <div className="positional-shape">{shape}</div>
                                                <div className="positional-label">{pos.charAt(0).toUpperCase() + pos.slice(1)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedLetter.example_word && (
                                <div className="alphabet-modal__example">
                                    <h3>Example Word</h3>
                                    <div className="example-word-card">
                                        <div className="example-word-main">
                                            <span className="example-word-text">{selectedLetter.example_word}</span>
                                            <button
                                                className="example-word-audio"
                                                onClick={() => playAudio(selectedLetter.example_word)}
                                                aria-label="Play example word audio"
                                            >
                                                <VolumeIcon size={16} />
                                            </button>
                                        </div>
                                        <div className="example-word-translit">
                                            ({selectedLetter.example_translation})
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AlphabetGrid;
